import Delete from '@material-ui/icons/Delete'
import dayjs from 'dayjs'
import { authSelectors } from 'ducks/auth'
import ImageDisplayField from 'elements/field/ImageDisplayField'
import { ColorInput } from 'elements/input/ColorInput'
import DateTimeInput from 'elements/input/DateTimeInput'
import { FieldValidationErrors } from 'elements/react-admin/FieldValidationErrors'
import { HighlightConfig } from 'opensolar-pageeditor'
import { Button, Link, Typography } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import { FC } from 'react'
import { BooleanInput, FileField, FileInput, ReferenceInput, SelectInput, TextInput } from 'react-admin'
import { useForm, useFormState } from 'react-final-form'
import { useSelector } from 'react-redux'
import InputAccordion from '../common/InputAccordion'
import MultipleCardField, { BtnWrapper } from '../common/MultipleCardField'
import { useBlockSource } from '../useBlockSource'

const styles = {
  componentMargin: {
    marginTop: 10,
    marginBottom: 10,
  },
}

const CTA_TYPE_OPTIONS = [
  { id: 'url', name: 'URL' },
  { id: 'file', name: 'File Download' },
]

interface FieldPropTypes {
  disabled: boolean
  assetSource: string
  record: any
  isExpoHomepageForm?: boolean | undefined
}

const HighlightsFields: FC<FieldPropTypes> = ({ disabled, record, assetSource, isExpoHomepageForm }) => {
  const translate = useTranslate()
  const source = useBlockSource('highlights')

  if (!source) return null
  return (
    <InputAccordion title={translate('Highlights')} source={source}>
      <MultipleCardField
        source={source}
        assetKey="highlights"
        render={(item: HighlightConfig & { id: string }, { index, deleteCardByID }) => {
          return (
            <HighlightInputs
              source={`${source}.${index}`}
              disabled={disabled}
              removeCard={() => {
                deleteCardByID(item.id, 'highlights')
              }}
              i={index}
              highlight={item}
              record={record}
              assetSource={assetSource}
              isExpoHomepageForm={isExpoHomepageForm}
            />
          )
        }}
      />
    </InputAccordion>
  )
}
interface PropTypes {
  source: string
  disabled: boolean
  removeCard: () => void
  highlight: HighlightConfig
  i: number
  record: any
  assetSource: string
  isExpoHomepageForm?: boolean | undefined
}

const HighlightInputs: FC<PropTypes> = ({
  source,
  disabled,
  removeCard,
  highlight,
  i,
  record,
  assetSource,
  isExpoHomepageForm,
}) => {
  const translate = useTranslate()
  const form = useForm()
  const formState = useFormState()
  const liveRecord = formState.values
  const isStaff = useSelector(authSelectors.getIsStaff)

  return (
    <InputAccordion title={highlight.title || `${translate('Card')} ${i + 1}`} source={source}>
      <TextInput source={`${source}.title`} label="Title" disabled={disabled} />
      <TextInput source={`${source}.description`} label="Description" disabled={disabled} multiline />
      <ImageDisplayField
        name={`${assetSource}.highlights.${highlight.id}.backgroundImage`}
        source={`${assetSource}.highlights.${highlight.id}.backgroundImage`}
        label={
          <div>
            <Typography>{translate('Background Image')}</Typography>
            <br />
            <Typography>
              {translate('The uploaded background image will appear on top of the background color.')}
            </Typography>
          </div>
        }
        defaultValue={null}
        accept="image/*"
        placeholder={<p>{translate('Drop your image here')}</p>}
        record={liveRecord}
        maxWidth={200}
      />
      <ColorInput
        source={`${source}.backgroundColor`}
        label="Background color"
        disabled={disabled}
        resource={undefined}
        validate={undefined}
      />
      <TextInput source={`${source}.cta.label`} label="CTA Label" disabled={disabled} />
      <SelectInput
        defaultValue={'url'}
        source={`${source}.cta.type`}
        label="CTA Type"
        disabled={disabled}
        choices={CTA_TYPE_OPTIONS}
      />
      {highlight.cta?.type && (
        <>
          {highlight.cta?.type === 'url' ? (
            <TextInput source={`${source}.cta.url`} label="CTA URL" disabled={disabled} />
          ) : (
            <FileInput
              source={`${assetSource}.highlights.${highlight.id}.ctaFile`}
              label={'CTA File Upload'}
              placeholder={<p>{translate('Click to upload or drag & drop a file here')}</p>}
            >
              <FileField source="src" title="title" />
            </FileInput>
          )}
        </>
      )}
      <BooleanInput
        label={'Promo Image'}
        source={`${source}.enablePromoImage`}
        disabled={disabled}
        onChange={(value: boolean) => {
          if (value) {
            form.change(`${source}.enableCountdown`, false)
            form.change(`${source}.alignTextCenter`, false)
          }
        }}
        style={styles.componentMargin}
      />
      {highlight.enablePromoImage && (
        <ImageDisplayField
          name={`${assetSource}.highlights.${highlight.id}.promoImage`}
          source={`${assetSource}.highlights.${highlight.id}.promoImage`}
          label={
            <div>
              <Typography>{translate('Product Image')}</Typography>
              <br />
              <Typography>
                {translate(
                  'This image will be displayed on top of your background, placed at the right side of the highlight card.'
                )}
              </Typography>
            </div>
          }
          defaultValue={null}
          accept="image/*"
          placeholder={<p>{translate('Drop your image here')}</p>}
          record={liveRecord}
          maxWidth={200}
        />
      )}
      <BooleanInput
        label={'Enable Countdown'}
        source={`${source}.enableCountdown`}
        disabled={disabled || highlight.enablePromoImage}
        style={styles.componentMargin}
      />
      {highlight.enableCountdown && (
        <>
          {
            //@ts-ignore
            <DateTimeInput source={`${source}.countdownDate`} label="Countdown Date" options={{ minDate: dayjs() }} />
          }
          <FieldValidationErrors source={`${source}.countdownDate`} />
        </>
      )}
      <BooleanInput
        label={'Full width?'}
        source={`${source}.fullWidth`}
        disabled={disabled}
        style={styles.componentMargin}
      />
      <BooleanInput
        label={'White text?'}
        source={`${source}.whiteText`}
        disabled={disabled}
        style={styles.componentMargin}
      />
      <BooleanInput
        label={'Align text to center?'}
        source={`${source}.alignTextCenter`}
        disabled={disabled || highlight.enablePromoImage}
        style={styles.componentMargin}
      />
      {isStaff && (
        <BooleanInput
          label={'UI 3.0 Styling?'}
          source={`${source}.ui3styling`}
          disabled={disabled}
          style={styles.componentMargin}
        />
      )}
      {isExpoHomepageForm ? (
        <ReferenceInput
          name={`${source}.brand`}
          source={`${source}.brand`}
          resource="brands"
          reference="brands"
          allowEmpty={true}
          perPage={1000}
          fullWidth={true}
          style={{ width: '100%' }}
          renderChildrenBeforeRecord={true}
          label="Select Partner"
        >
          <SelectInput
            source={'url'}
            optionText="name"
            optionValue="url"
            disabled={disabled}
            helperText={<Link href="/brands">Configure Brands</Link>}
          />
        </ReferenceInput>
      ) : (
        <BooleanInput
          label={'Add Logo'}
          source={`${source}.show_brand_logo`}
          disabled={disabled}
          style={styles.componentMargin}
        />
      )}
      <BtnWrapper>
        <Button
          onClick={removeCard}
          startIcon={<Delete />}
          variant="contained"
          color="error"
          style={styles.componentMargin}
        >
          {translate('Delete Card')}
        </Button>
      </BtnWrapper>
    </InputAccordion>
  )
}

export default HighlightsFields
